import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AssetGroup, Hoid } from "./data";

import { ArrowDownIcon, InfoIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { buildAssetClassOfSymbol } from "./RebateSplitOverride/catalogue";
import { findShadowedAssetClass } from "./RebateSplitOverride/helpers";

type RebateSplitOverrideSectionProps = {
  hoid: Hoid;
  /**
   * Override for the section's own `-mt-2` (tuned for the desktop drawer's
   * gap unit — see `ProgramDetailsDrawer.tsx`). Defaults to unset so desktop
   * is unaffected; the mobile sheet passes its own top-rule pattern instead.
   */
  className?: string;
  assets?: AssetGroup[] | null;
};

const SO_YOU = "bg-surface-brand-default";
const SO_PARTY = ["bg-surface-emphasis", "bg-surface-accent-blue-subtler"];

/**
 * Read-only, collapsible override list — shown only when the program carries
 * `symbolOverrides`. No rule-editing UI (that belongs to a future Create/Edit
 * program wizard step, out of scope for PART-3825).
 */
export const RebateSplitOverrideSection = ({
  hoid,
  className,
  assets,
}: RebateSplitOverrideSectionProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rules = hoid.symbolOverrides;
  const lookupAssetClass = buildAssetClassOfSymbol(assets);

  if (!rules || rules.length === 0) return null;

  const preview =
    rules
      .slice(0, 3)
      .map((r) => r.scopeValue)
      .join(", ") + (rules.length > 3 ? `, +${rules.length - 3} more` : "");

  return (
    <section
      className={clsx("-mt-2 flex flex-col gap-4", className)}
      aria-label={t("subIBReport_rebateSplitOverride", "Override rules")}
    >
      <div className="overflow-hidden rounded-xl border border-stroke-primary">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={clsx(
            "flex w-full items-center gap-3 p-4 text-left hover:bg-surface-tonal-default",
            open && "border-b border-stroke-primary",
          )}
        >
          <span className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="inline-flex items-center gap-2">
              <Typo element="span" type="labelSM">
                {t("subIBReport_rebateSplitOverride", "Override rules")}
              </Typo>
              <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-surface-accent-blue-subtler px-1">
                <Typo
                  element="span"
                  type="label2XS"
                  textColor="text-text-accent-blue-boldest"
                >
                  {t("subIBReport_overrideRuleCount", {
                    count: rules.length,
                    defaultValue_one: "{{count}} rule",
                    defaultValue_other: "{{count}} rules",
                  })}
                </Typo>
              </span>
            </span>
            {!open && (
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
              >
                {preview}
              </Typo>
            )}
          </span>
          <span
            className={clsx(
              "flex h-8 w-8 shrink-0 items-center justify-center text-text-secondary transition-transform duration-200",
              open && "rotate-180",
            )}
          >
            <ArrowDownIcon className="h-3 w-3" aria-hidden="true" />
          </span>
        </button>

        {open && (
          <div className="flex flex-col gap-4 p-4">
            {rules.map((rule, i) => {
              const shadowedAssetClass = findShadowedAssetClass(
                rule.scopeType === "symbol" ? rule.scopeValue : null,
                rules,
                lookupAssetClass,
              );

              return (
                <div
                  key={`${rule.scopeType}-${rule.scopeValue}`}
                  className={clsx(
                    "flex flex-col gap-3",
                    i > 0 && "border-t border-stroke-primary pt-4",
                  )}
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Typo element="span" type="labelSM">
                      {rule.scopeValue}
                    </Typo>
                    <Typo
                      element="span"
                      type="bodySM"
                      textColor="text-text-secondary"
                    >
                      {rule.scopeType === "symbol"
                        ? t("subIBReport_overrideScopeSymbol", "Symbol")
                        : t(
                            "subIBReport_overrideScopeAssetClass",
                            "Asset class",
                          )}
                    </Typo>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={clsx("h-3.5 w-3.5 shrink-0 rounded", SO_YOU)}
                      />
                      <Typo element="span" type="bodySM">
                        {t("subIBReport_you", "You")}
                      </Typo>
                      <Typo
                        element="span"
                        type="bodySM"
                        textColor="text-text-secondary"
                        className="ml-2"
                      >
                        {`${rule.masterSplitPct}%`}
                      </Typo>
                    </span>
                    {rule.partnerSplits.map((split, idx) => {
                      const partner = hoid.partners.find(
                        (p) => p.subIbId === split.subIbId,
                      );
                      return (
                        <span
                          key={split.subIbId}
                          className="inline-flex min-w-0 max-w-full items-center gap-2"
                        >
                          <span
                            className={clsx(
                              "h-3.5 w-3.5 shrink-0 rounded",
                              SO_PARTY[idx % SO_PARTY.length],
                            )}
                          />
                          <Typo
                            element="span"
                            type="bodySM"
                            className="truncate"
                          >
                            {partner?.name ??
                              t("subIBReport_defaultSubIb", "Sub IB")}
                          </Typo>
                          <Typo
                            element="span"
                            type="bodySM"
                            textColor="text-text-secondary"
                            className="ml-2 shrink-0"
                          >
                            {`${split.splitPct}%`}
                          </Typo>
                        </span>
                      );
                    })}
                  </div>
                  {shadowedAssetClass && (
                    <span className="inline-flex items-center gap-2">
                      <span className="shrink-0">
                        <InfoIcon
                          className="h-4 w-4 [&_path]:stroke-icon-brand-default"
                          aria-hidden="true"
                          focusable="false"
                        />
                      </span>
                      <Typo
                        element="span"
                        type="bodyXS"
                        textColor="text-text-secondary"
                      >
                        {t(
                          "subIBReport_overrideShadowNote",
                          "Overrides the {{assetClass}} rule",
                          { assetClass: shadowedAssetClass },
                        )}
                      </Typo>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
